<script setup lang="ts">
import {
  mdiCheck,
  mdiExclamationThick,
  mdiMenuDown,
} from '@quasar/extras/mdi-v7';
import { useDialogPluginComponent } from 'quasar';
import { t } from 'boot/i18n';

type Props = {
  title: string;
  subTitle?: string;
  dark: boolean;
  correctAnswer?: string | string[];
  yourAnswer?: string;
  message?: string;
};

defineProps<Props>();

const model = defineModel<boolean>();

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK } = useDialogPluginComponent();
function onOKClick() {
  onDialogOK();
}
</script>

<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable vue/no-v-text-v-html-on-component -->
<template>
  <q-dialog
    ref="dialogRef"
    v-model:model-value="model"
    backdrop-filter="blur(4px)"
    @hide="onDialogHide"
  >
    <q-card
      :dark="dark"
      class="q-dialog-plugin"
    >
      <q-card-section class="text-h6 text-border">
        <q-icon
          :name="mdiExclamationThick"
          color="negative"
        />
        {{ title }}
      </q-card-section>
      <q-card-section
        v-if="!!subTitle?.length"
        v-html="subTitle"
      />
      <q-card-section v-if="!!correctAnswer || !!yourAnswer">
        <slot>
          <!-- Default content -->
          <q-list
            bordered
            :dark="dark"
            separator
          >
            <q-item v-if="correctAnswer !== ''">
              <q-item-section>
                <q-item-label
                  caption
                  class="text-border"
                  >{{ t('CORRECT_ANSWER') }}</q-item-label
                >
                <q-item-label
                  v-if="
                    typeof correctAnswer === 'string' &&
                    correctAnswer?.length > 0
                  "
                  class="text-body2 text-bold text-positive feedback-html"
                  v-html="correctAnswer"
                ></q-item-label>
                <q-item-label
                  v-else-if="
                    Array.isArray(correctAnswer) && correctAnswer?.length === 1
                  "
                  class="text-body2 text-bold text-positive"
                >
                  {{ correctAnswer[0] }}
                </q-item-label>
                <q-select
                  v-else-if="
                    Array.isArray(correctAnswer) && correctAnswer?.length > 1
                  "
                  :model-value="correctAnswer[0]"
                  hide-bottom-space
                  :options="correctAnswer"
                  popup-content-class="bg-dark text-accent"
                  color="positive"
                  hide-dropdown-icon
                  borderless
                  dense
                >
                  <template #selected>
                    <span class="text-positive">{{ correctAnswer[0] }}</span>
                  </template>
                  <template #append>
                    <q-icon
                      :name="mdiMenuDown"
                      color="positive"
                    />
                  </template>
                </q-select>
              </q-item-section>
              <q-item-section side>
                <q-icon
                  :name="mdiCheck"
                  color="positive"
                />
              </q-item-section>
            </q-item>

            <q-item v-if="yourAnswer">
              <q-item-section>
                <q-item-label
                  v-if="correctAnswer !== ''"
                  caption
                  class="text-border"
                  >{{ t('YOUR_ANSWER') }}</q-item-label
                >
                <q-item-label class="text-body2 text-bold text-negative">
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <div
                    class="feedback-html"
                    v-html="yourAnswer"
                  ></div>
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item
              v-if="message"
              class="bg-grey-2"
              :class="{ 'bg-grey-9': dark }"
            >
              <q-item-section>
                <q-item-label class="text-body1">{{ message }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </slot>
      </q-card-section>
      <q-card-actions
        align="right"
        class="q-px-md"
      >
        <q-btn
          text-color="dark"
          color="accent"
          label="OK"
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
